import { useEffect, useRef } from "react";
import type { OnboardingProvisionResponse, OnboardingStatusResponse } from "../../types/argus";
import { useApiMutation } from "../use-api-mutation";
import { useApiQuery } from "../use-api-query";
import { useAuthToken } from "../use-auth-token";

/**
 * Onboarding provision + status machine.
 *
 * Fires `POST /client/argus/onboarding/provision` once on mount (BE is
 * idempotent; the ref-based dedup here just avoids a redundant trip when
 * React 18 StrictMode double-mounts in dev). Then polls
 * `GET /client/argus/onboarding/status` every 3s until both
 * `instance_ready` and `session_ready` flip to true.
 *
 * Auth-token gating: we only fire the provision mutation once `useAuthToken`
 * has resolved a non-null token. Without this gate, the initial render fires
 * the mutation with a `null` Bearer token and `useApi` throws
 * `MissingAuthTokenError` (v1 Phase 5 race). Once Clerk's session is ready
 * the token becomes non-null and the dedup ref ensures we still only fire
 * once.
 */
export interface UseArgusOnboardingResult {
  instanceId: string | null;
  /**
   * ArgusSession DB row PK. Use this for cope-api calls to
   * `/client/argus/instances/:instance_id/sessions/:id`. **Not** the
   * right value to pass as `?session=...` to `/argus` — the UI there
   * looks up via the gateway `agent_session_id`. Use `agentSessionId`
   * for navigation.
   */
  sessionId: string | null;
  /**
   * Gateway-assigned session id (ArgusSession.agent_session_id). Non-null
   * exactly when `sessionReady` is true — this is what the `/argus` page
   * and its WebSocket/SSE subscriptions expect.
   */
  agentSessionId: string | null;
  instanceStatus: OnboardingStatusResponse["instance_status"];
  instanceReady: boolean;
  sessionReady: boolean;
  flags: OnboardingStatusResponse["flags"] | null;
  isProvisioning: boolean;
  provisionError: Error | null;
  /** Manually re-fire the provision mutation (error-phase Retry). */
  retryProvision: () => void;
  /** Full status payload (null until the first poll lands). */
  status: OnboardingStatusResponse | null;
}

export function useArgusOnboarding(): UseArgusOnboardingResult {
  const token = useAuthToken();

  // Dedup ref: survives StrictMode's dev-only double-mount so we don't
  // double-fire the provision mutation. BE is idempotent — this is just
  // a network hygiene thing.
  const provisionFiredRef = useRef(false);

  const provisionMutation = useApiMutation<void, OnboardingProvisionResponse>(
    "POST",
    "/client/argus/onboarding/provision",
  );

  // Kick provision on mount, once we have an auth token. Mutation itself
  // ignores `token` changes; the ref guards against repeat fires.
  // Re-adding `provisionMutation.mutate` as a dep would re-fire provision
  // on every render (the function identity changes).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mutation ref is a stable handle
  useEffect(() => {
    if (!token) return;
    if (provisionFiredRef.current) return;
    provisionFiredRef.current = true;
    provisionMutation.mutate({});
  }, [token]);

  const statusQuery = useApiQuery<OnboardingStatusResponse>("/client/argus/onboarding/status", {
    enabled: !!token,
    // Poll every 3s while the instance/session aren't both ready. Once
    // both flip, stop polling — we don't want a permanently-open loop
    // if the user lingers on the page after the CTA enables.
    refetchInterval: (query) => {
      const data = query.state.data as OnboardingStatusResponse | undefined;
      if (!data) return 3_000;
      if (data.instance_ready && data.session_ready) return false;
      return 3_000;
    },
  });

  const status = statusQuery.data ?? null;

  return {
    instanceId: status?.instance_id ?? null,
    sessionId: status?.session_id ?? null,
    agentSessionId: status?.agent_session_id ?? null,
    instanceStatus: status?.instance_status ?? null,
    instanceReady: status?.instance_ready ?? false,
    sessionReady: status?.session_ready ?? false,
    flags: status?.flags ?? null,
    isProvisioning: provisionMutation.isPending,
    provisionError: (provisionMutation.error as Error | null) ?? null,
    retryProvision: () => {
      // Reset the dedup guard so Retry can actually fire again.
      provisionFiredRef.current = true;
      provisionMutation.reset();
      provisionMutation.mutate({});
    },
    status,
  };
}
